"use client";

import TabContainer from "ui/atoms/Container/TabContainer";
import { Todo } from "shared/todos/Todo";
import useTodosContext from "shared/todos/useTodosContext";
import { selectCompletedTodos } from "shared/todos/useTodosReducer";
import { descriptionsByGame } from "shared/games/allTechniqueGameDescriptions";
import classNames from "classnames";
import Image from "next/image";
import * as routes from "routes";
import Link from "util/Link";
import { Difficulty } from "shared/games/Difficulty";
import { CheckIcon } from "@heroicons/react/24/solid";
import Button from "ui/atoms/Button/Button";
import prettyInstrument from "util/formatters/prettyInstrument";
import { instrumentsConfig } from "shared/games/instruments/instrumentsConfig";
import { useMountEffect } from "@react-hookz/web";
import trackPage from "shared/analytics/mixpanel/trackPage";
import { pages } from "shared/analytics/mixpanel/constants";
import { usePathname } from "next/navigation";
import { PageConfig } from "./getPageConfig";
import TodosContextProvider from "shared/todos/TodosContextProvider";
import outOfLives from "shared/user/lives/outOfLives";
import useAuth from "util/useAuth/useAuth";
import { GeneralPageLayout } from "ui/templates/PageLayouts";

interface Props {
  todos: Todo[];
  title: string;
  subtitle: string;
  config: PageConfig;
}

export default function PageClientWrapper(props: Props) {
  return (
    <TodosContextProvider todos={props.todos}>
      <PageClient {...props} />
    </TodosContextProvider>
  );
}

function PageClient({ todos, title, subtitle, config }: Props) {
  const auth = useAuth();
  const [state] = useTodosContext();
  const completedTodos = selectCompletedTodos(state);
  const currentPath = usePathname() ?? routes.earTraining.mixing.home;

  useMountEffect(() => {
    trackPage(pages.earTrainingShow(config));
  });

  const byDifficulty: {
    [difficulty in Difficulty]: { difficulty: difficulty; todos: Todo[] };
  } = {
    easy: {
      difficulty: Difficulty.easy,
      todos: todos.filter((todo) => todo.difficulty === Difficulty.easy),
    },
    medium: {
      difficulty: Difficulty.medium,
      todos: todos.filter((todo) => todo.difficulty === Difficulty.medium),
    },
    hard: {
      difficulty: Difficulty.hard,
      todos: todos.filter((todo) => todo.difficulty === Difficulty.hard),
    },
  };

  /**
   * Get a list of todo ids starting with the given todo going up in difficulty order
   */
  const getQueueStartingWithTodo = (todo: Todo) => {
    let difficultyLevelsAfter: Todo[] = [];

    // All difficulty todos after the current difficulty of the todo
    if (todo.difficulty === Difficulty.easy) {
      difficultyLevelsAfter = [
        ...byDifficulty.medium.todos,
        ...byDifficulty.hard.todos,
      ];
    } else if (todo.difficulty === Difficulty.medium) {
      difficultyLevelsAfter = [...byDifficulty.hard.todos];
    }

    const index = byDifficulty[todo.difficulty].todos.indexOf(todo);

    return [
      ...byDifficulty[todo.difficulty].todos.slice(index),
      ...difficultyLevelsAfter,
    ].map((todo) => todo.id);
  };

  const upNextId = [
    ...byDifficulty.easy.todos,
    ...byDifficulty.medium.todos,
    ...byDifficulty.hard.todos,
  ].find((todo) => !completedTodos.has(todo.id))?.id;

  const locked = outOfLives(auth);
  // Used for SEO so don't show when signed in
  const subtitleParagraphs =
    auth.type === "loggedout"
      ? subtitle.split(/\n+/).filter((text) => text.trim().length > 0)
      : [];

  return (
    <GeneralPageLayout auth={auth} className="md:pt-10 pt-6 pb-20">
      <h1
        className={classNames(
          "font-brik text-5xl text-white text-center leading-none",
          "mb-6"
        )}
      >
        {title}
      </h1>

      {subtitleParagraphs.length > 0 && (
        <h3 className={"text-white text-center mb-8"}>
          {subtitleParagraphs.map((p, i) => {
            const isLast = i === subtitleParagraphs.length - 1;

            return (
              <p key={i} className={classNames({ "mb-1": !isLast })}>
                {p}
              </p>
            );
          })}
        </h3>
      )}

      {Object.values(byDifficulty).map(({ difficulty, todos }) => (
        <TabContainer
          key={difficulty}
          containerClassName={"mb-8 md:mb-16 text-white"}
          containerPadding={"p-4"}
          title={`${difficulty} mode`}
          subtitle={`${byDifficulty[difficulty].todos.length} To-Dos`}
          locked={locked}
        >
          <div
            className={
              "grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-4"
            }
          >
            {todos.map((todo, i) => (
              <TodoCard
                upNext={todo.id === upNextId}
                key={todo.id}
                currentPath={currentPath}
                todo={todo}
                completed={
                  state.state === "loading" ? true : completedTodos.has(todo.id)
                }
                getTodoIdList={() => getQueueStartingWithTodo(todo)}
              />
            ))}
          </div>
        </TabContainer>
      ))}
    </GeneralPageLayout>
  );
}

function TodoCard({
  todo: { game, looop_instrument },
  completed,
  getTodoIdList,
  currentPath,
  upNext,
}: {
  todo: Todo;
  completed: boolean;
  getTodoIdList: () => number[];
  currentPath: string;
  upNext: boolean;
}) {
  const gameDesc = descriptionsByGame[game];

  const Divider = () => (
    <hr
      className={classNames(
        "my-2 border-1 rounded",
        completed ? "border-gray-500" : "border-black"
      )}
    />
  );

  return (
    <div
      className={classNames(
        "p-2 rounded border",
        completed
          ? "bg-black text-gray-500 border-gray-500"
          : "bg-white text-black border-white",
        upNext && "animate-bounce"
      )}
    >
      <div className={"flex justify-between mb-4"}>
        <div className={"flex items-center"}>
          <div
            title={
              completed
                ? "This Todo is Completed 🎉"
                : "Click to work on this Todo"
            }
            className={classNames(
              "w-6 h-6 border-2 rounded-full relative flex items-center justify-center mr-2",
              completed
                ? "bg-emerald-500 border-emerald-500 stroke-white"
                : "border-black stroke-black bg-white"
            )}
          >
            {completed ? (
              <>
                <CheckIcon className={"h-4 w-4 stroke-2"} />
              </>
            ) : (
              <span />
            )}
          </div>
        </div>

        <div className={"text-xl font-bold leading-none"}>
          {completed ? "Done" : "To-Do"}
        </div>
      </div>

      {/* Game Desc */}
      <div className={"flex items-center"}>
        {/* image wrapper */}
        <div
          className={classNames(
            "h-10 w-20 rounded relative overflow-hidden shadow-xl mr-2 border",
            completed ? "border-gray-500" : "border-white"
          )}
        >
          <Image
            src={`/images/game-screenshots/${gameDesc.game}.png`}
            width={80}
            height={80}
            className="object-cover absolute left-0 top-0 scale-105"
            alt=""
          />
        </div>

        <div className={"leading-none"}>
          <div className={"font-bold"}>Game:</div>
          <div>{gameDesc.shortTitle}</div>
        </div>
      </div>

      <Divider />

      {/* Instrument Desc */}
      <div className={"flex items-center mb-4"}>
        {/* image wrapper */}
        <div
          className={classNames(
            "h-10 w-20 rounded relative overflow-hidden shadow-xl mr-2 bg-black p-2 flex items-center justify-center border",

            completed ? "border-gray-500" : "border-white"
          )}
        >
          {instrumentsConfig[looop_instrument].icon({
            className: "h-full fill-white",
          })}
        </div>

        <div className={"leading-none"}>
          <div className={"font-bold"}>Instrument:</div>
          <div>{prettyInstrument(looop_instrument)}</div>
        </div>
      </div>

      {/* <Divider /> */}

      <div className={"flex justify-center"}>
        <Button
          theme={completed ? "default" : "blue"}
          outline={completed || !upNext}
          block
          as={Link}
          href={routes.earTraining.mixing.todosQueryParams({
            type: "by-id",
            todoIds: getTodoIdList(),
            back: currentPath,
          })}
          onClick={() => {
            // TODO: track click
          }}
        >
          {completed ? "Answer Again" : "Start Here!"}
        </Button>
      </div>
    </div>
  );
}
